<template>
  <div class="layout">
    <m-confirm ref="confirm" mode="primary" />
    <m-info ref="info" />

    <div class="layout-header">
      <h2 class="layout-title">{{ id ? "Редактирование анкеты" : "Новая анкета" }}</h2>
      <div class="layout-header-actions">
        <div class="layout-header-action">
          <c-button
            size="small"
            :responsive="true"
            icon="back"
            type="with-icon"
            label="К списку анкет"
            @click="$router.push({ name: 'profiles' })"
          />
        </div>
      </div>
    </div>

    <div class="layout-content">
      <profile-form :profile-data="profileData" @updated="updateProfileData" />
    </div>

    <div v-if="id">
      <div v-if="loading">
        <c-loader :active="loading" :height="450" label="Данные загружаются" />
      </div>
      <div v-else>
        <c-tabs :active="activeProfileSection" :items="profileSections" @change="activeProfileSection = $event" />

        <profile-section
          :profile-id="id"
          :profile-section="activeProfileSection"
          :profile-tabs="tabs"
          @tabCreated="addTab"
          @tabUpdated="updateTab"
          @tabRemoved="removeTab"
          @tabsResorted="resortTabs"
          @fieldCreated="addField"
          @fieldUpdated="updateField"
          @fieldRemoved="removeField"
          @fieldMoved="moveField"
          @copySection="copySection"
        />
      </div>
    </div>

    <top-button root=".layout"></top-button>
  </div>
</template>

<script>
import CButton from "@/components/controls2/Button";
import CTabs from "@/components/tabs";
import TopButton from "@/components/top-button";
import CLoader from "@/components/loader";
import MConfirm from "@/components/modals/confirm-modal";
import MInfo from "@/components/modals/info-modal";

import ProfileForm from "./components/ProfileForm";
import ProfileSection from "./components/ProfileSection";

import {
  PROFILE_SECTION_EMPLOYEE,
  PROFILE_SECTION_CANDIDATE,
  PROFILE_SECTION_CANDIDATE_SHORT,
} from "@/domains/profile/constants/profile-sections";
import { ProfileData } from "../models/ProfileData";
import {
  addProfileTabField,
  ProfileTabModel,
  removeProfileTabField,
  updateProfileTabData,
  updateProfileTabField,
} from "../models/ProfileTabModel";

export default {
  components: { MConfirm, MInfo, CButton, TopButton, CLoader, CTabs, ProfileForm, ProfileSection },

  data() {
    return {
      id: null,
      profileData: null,
      loading: false,

      profileSections: [
        { value: PROFILE_SECTION_EMPLOYEE, label: "Анкета сотрудника", id: "employee" },
        { value: PROFILE_SECTION_CANDIDATE, label: "Анкета кандидата", id: "candidate" },
        { value: PROFILE_SECTION_CANDIDATE_SHORT, label: "Краткая анкета кадидата", id: "candidate-short" },
      ],
      activeProfileSection: PROFILE_SECTION_EMPLOYEE,

      tabs: [],
    };
  },

  watch: {
    activeProfileSection(v) {
      const { query } = this.$route;

      const section = this.profileSections.find((s) => s.value === v);
      if (!section) return;
      if (query.section === section.id) return;

      this.$router.push({
        query: {
          ...query,
          section: section.id,
        },
      });
    },
  },

  created() {
    const { id } = this.$route.params;
    const { section } = this.$route.query;

    if (section) {
      const activeSection = this.profileSections.find((s) => s.id === section);
      if (activeSection) this.activeProfileSection = activeSection.value;
    }

    if (id) {
      this.id = id;
      this.loadProfile();
    }
  },

  methods: {
    loadProfile() {
      this.loading = true;

      this.$store.dispatch("profile/getProfile", this.id).then((data) => {
        this.initProfileData(data);
        this.loading = false;
      });
    },

    initProfileData(data) {
      this.profileData = ProfileData(data);
      const tabs = this.profileData.tabs.map((tab) => ProfileTabModel(tab));
      tabs.sort((a, b) => a.sort - b.sort);
      this.tabs = tabs;
    },

    updateProfileData(data) {
      this.initProfileData(data);

      if (!this.id) {
        const { id } = data;

        this.id = id;
        this.$router.push({ name: "edit-profile", params: { id } });
      }
    },

    getTabById(tabId) {
      return this.tabs.find((tab) => tab.id === tabId);
    },

    addTab({ tabData }) {
      const tabModel = ProfileTabModel(tabData);
      this.tabs.push(tabModel);
    },
    updateTab({ tabData }) {
      const tab = this.getTabById(tabData.id);
      if (tab) {
        updateProfileTabData(tab, tabData);
      }
    },
    removeTab({ tabId }) {
      this.tabs = this.tabs.filter((tab) => tab.id !== tabId);
    },
    resortTabs(order) {
      this.$store.dispatch("profile/sortTabs", order);

      const orders = {};
      order.forEach((tabId, i) => {
        orders[tabId] = i + 1;
      });

      const tabs = [...this.tabs];

      tabs.forEach((tab) => {
        if (orders[tab.id]) tab.sort = orders[tab.id];
      });

      tabs.sort((a, b) => a.sort - b.sort);

      this.tabs = tabs;
    },

    addField({ fieldData, tabId }) {
      const tab = this.getTabById(tabId);
      addProfileTabField(tab, fieldData);
    },
    updateField({ fieldData, tabId, oldTabId }) {
      const tab = this.getTabById(tabId);

      if (oldTabId && tabId !== oldTabId) {
        this.moveField({ field: fieldData, oldTabId, newTabId: tabId });
      } else {
        updateProfileTabField(tab, fieldData);
      }
    },
    removeField({ fieldId, tabId }) {
      const tab = this.getTabById(tabId);
      removeProfileTabField(tab, fieldId);
    },
    moveField({ field, oldTabId, newTabId }) {
      const oldTab = this.getTabById(oldTabId);
      const newTab = this.getTabById(newTabId);

      removeProfileTabField(oldTab, field.id);
      addProfileTabField(newTab, field);
    },
    copySection({ from, to }) {
      const texts = {
        [PROFILE_SECTION_EMPLOYEE]: "Анкета сотрудника",
        [PROFILE_SECTION_CANDIDATE]: "Анкета кандидата",
        [PROFILE_SECTION_CANDIDATE_SHORT]: "Краткая анкета кандидата",
      };

      const textsGen = {
        [PROFILE_SECTION_EMPLOYEE]: "анкеты сотрудника",
        [PROFILE_SECTION_CANDIDATE]: "анкеты кандидата",
        [PROFILE_SECTION_CANDIDATE_SHORT]: "краткой анкеты кандидата",
      };

      const isOriginalSectionNotEmpty = this.tabs.some((tab) => tab.profileSection === from.toString());

      if (!isOriginalSectionNotEmpty) {
        this.$refs.info.open({
          title: "Копировать поля",
          text: `<b>${texts[from]}</b> пустая. Для копирования полей из этой анкеты необходимо её заполнить.`,
        });
      } else {
        this.$refs.confirm.open({
          title: "Копировать поля",
          text: `Текущие настройки <b>${textsGen[to]}</b> будут полностью заменены настройками <b>${textsGen[from]}</b>. Продолжить изменение?`,
          confirm: "Копировать",
          onConfirm: async () => {
            const { success } = await this.$store.dispatch("profile/copyProfileSection", {
              id: this.id,
              from,
              to,
            });

            if (success) {
              this.$notify({
                group: "success-message",
                data: {
                  textMessage: `Настройки типа анкеты <b>${texts[to]}</b> успешно обновлены`,
                },
              });
              this.loadProfile();
            } else {
              this.$notify({
                group: "error-message",
                data: {
                  textMessage: "Произошла ошибка. Попробуйте ещё раз.",
                },
              });
            }
          },
        });
      }
    },
  },
};
</script>

<style scoped>
.layout-header {
  height: auto;
  min-height: 30px;
}
</style>
